// File: frontend/src/utils/validation.js

// Regular expressions for validation - keeping the original patterns
const PHONE_PATTERNS = {
  '+1': /^\+1[2-9]\d{2}[2-9]\d{6}$/, // US/Canada: +1 + valid area code + 7 digits
  '+31': /^\+31[1-9]\d{8}$/, // Netherlands
  '+33': /^\+33[1-9]\d{8}$/, // France
  '+34': /^\+34[6789]\d{8}$/, // Spain
  '+44': /^\+44[1-9]\d{9}$/, // UK
  '+49': /^\+49[1-9]\d{10}$/, // Germany
  '+52': /^\+52[1-9]\d{9}$/, // Mexico
  '+90': /^\+90[5]\d{9}$/, // Turkey (mobile numbers start with 5)
  '+353': /^\+353[1-9]\d{8}$/, // Ireland
};

const VALID_COUNTRY_CODES = ['+1', '+31', '+33', '+34', '+44', '+49', '+52', '+90', '+353'];

// Country-specific area code lengths
const AREA_CODE_LENGTHS = {
  '+1': 3,  // US/Canada
  '+90': 3, // Turkey
  '+44': 3, // UK
  '+31': 2, // Netherlands
  '+33': 2, // France
  '+34': 2, // Spain
  '+49': 3, // Germany
  '+52': 2, // Mexico
  '+353': 2, // Ireland
};

// More comprehensive email validation from the OLD version
export const validateEmail = (email) => {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})$/;
  return re.test(String(email).toLowerCase());
};

// Keeping the simpler version as an alternative
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Basic phone validation from OLD version
export const validatePhone = (phone) => {
  const phoneRegex = /^\+?[1-9]\d{7,14}$/;
  return phone && phoneRegex.test(phone.replace(/\D/g, ''));
};

export const parsePhoneNumber = (phone_number) => {
  if (!phone_number) return null;

  // Remove any whitespace, dashes, parentheses
  const cleanNumber = phone_number.replace(/[\s\-\(\)]/g, '');
  
  // Handle special case for autofill where number might not have + prefix
  let formattedNumber = cleanNumber;
  if (!formattedNumber.startsWith('+')) {
    // If it starts with a country code digit, add +
    if (/^[1-9]/.test(formattedNumber)) {
      formattedNumber = `+${formattedNumber}`;
    } else {
      // Default to North American format if no country code
      formattedNumber = `+1${formattedNumber}`;
    }
  }

  // Special case for North American numbers (most common in autofill)
  if (formattedNumber.startsWith('+1') && formattedNumber.length >= 11) {
    // +1 followed by 10 digits
    const areaCode = formattedNumber.substring(2, 5);
    const phoneNumber = formattedNumber.substring(5);
    
    // Basic validation for North American numbers
    if (/^[2-9]\d{2}$/.test(areaCode) && /^[2-9]\d{6}$/.test(phoneNumber)) {
      return {
        country_phone_code: '+1',
        area_code: areaCode,
        phone_number: phoneNumber,
        mobile_phone_number: `+1${areaCode}${phoneNumber}`
      };
    }
  }

  // Find matching country code
  const country_phone_code = VALID_COUNTRY_CODES.find(code => formattedNumber.startsWith(code));
  if (!country_phone_code) {
    // If no valid country code found, try to make a best guess for North American
    if (formattedNumber.length >= 10) {
      const areaCode = formattedNumber.substring(0, 3);
      const phoneNumber = formattedNumber.substring(3);
      
      return {
        country_phone_code: '+1',
        area_code: areaCode,
        phone_number: phoneNumber,
        mobile_phone_number: `+1${areaCode}${phoneNumber}`
      };
    }
    return null;
  }

  // Parse based on country code
  const remaining = formattedNumber.slice(country_phone_code.length);
  
  // Get country-specific area code length
  const areaCodeLength = AREA_CODE_LENGTHS[country_phone_code];
  if (!areaCodeLength) return null;

  const area_code = remaining.slice(0, areaCodeLength);
  const number = remaining.slice(areaCodeLength);

  // For autofill, we'll be more lenient with validation
  // but still do a basic check
  if (area_code.length !== areaCodeLength || number.length < 4) {
    return null;
  }

  return {
    country_phone_code: country_phone_code,
    area_code: area_code,
    phone_number: number,
    mobile_phone_number: formattedNumber
  };
};

export const isValidPhoneNumber = (phone_number) => {
  if (!phone_number) return false;

  // Remove any whitespace and ensure there's a leading +
  const cleanNumber = phone_number.replace(/\s+/g, '');
  if (!cleanNumber.startsWith('+')) return false;

  // Find matching country code and pattern
  const country_phone_code = VALID_COUNTRY_CODES.find(code => cleanNumber.startsWith(code));
  if (!country_phone_code) return false;

  // Test against country-specific pattern
  return PHONE_PATTERNS[country_phone_code].test(cleanNumber);
};

// Name validation from OLD version
export const validateName = (name) => {
  return name && name.trim().length >= 2;
};

// Company name validation from OLD version
export const validateCompanyName = (name) => {
  return name && name.trim().length >= 2 && name.trim().length <= 100;
};

export const validateRequired = (value, fieldName) => {
  if (!value || (typeof value === 'string' && value.trim().length === 0)) {
    return `${fieldName} is required`;
  }
  return null;
};

export const validatePhoneNumber = (phone_number) => {
  if (!phone_number) return 'Phone number is required';
  
  const cleanNumber = phone_number.replace(/\s+/g, '');
  if (!cleanNumber.startsWith('+')) return 'Phone number must start with +';
  
  const country_phone_code = VALID_COUNTRY_CODES.find(code => cleanNumber.startsWith(code));
  if (!country_phone_code) return 'Invalid country code';
  
  if (!isValidPhoneNumber(cleanNumber)) {
    return 'Invalid phone number format for the selected country';
  }
  
  return null;
};

// Enhanced name formatting from the OLD version
export const formatName = (name) => {
  if (!name) return name;
  
  // Split on various delimiters that might appear in names
  const nameParts = name.split(/(['\s-])/);
  
  return nameParts
    .map((part, index) => {
      // Skip empty parts and delimiters
      if (!part || part.match(/['\s-]/)) return part;

      // Special prefixes that should be in a specific case
      const specialPrefixes = {
        'mc': 'Mc',
        'mac': 'Mac',
        'o\'': 'O\'',
        'van': 'van',
        'von': 'von',
        'de': 'de',
        'la': 'la',
        'le': 'Le',
        'den': 'den',
        'der': 'der',
        'di': 'di',
        'du': 'du',
        'san': 'San',
        'st': 'St',
        'st.': 'St.'
      };

      // Convert part to lowercase for comparison
      const lowerPart = part.toLowerCase();

      // Check if this part is a special prefix
      if (specialPrefixes[lowerPart]) {
        return specialPrefixes[lowerPart];
      }

      // Handle Roman numerals (e.g., III, IV, VIII)
      const romanNumeralPattern = /^(i{1,3}|iv|v|vi{1,3}|ix|x|xi{1,3}|xiv|xv|xvi{1,3})$/i;
      if (romanNumeralPattern.test(lowerPart)) {
        return lowerPart.toUpperCase();
      }

      // General case: capitalize first letter, lowercase rest
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join('');
};

// Example usage:
// console.log(formatName('john')); // "John"
// console.log(formatName('SMITH')); // "Smith"
// console.log(formatName('von braun')); // "von Braun"
// console.log(formatName('macarthur')); // "MacArthur"
// console.log(formatName('o\'connor')); // "O'Connor"
// console.log(formatName('mary-jane')); // "Mary-Jane"
// console.log(formatName('jean-pierre')); // "Jean-Pierre"
// console.log(formatName('st. john')); // "St. John"
// console.log(formatName('william iii')); // "William III"